import { JOB_SUMMARY_DEFAULT_SORT, JOB_SUMMARY_TABS, JobSummaryRequest, JobSummaryResponse, TimeRangeEnum } from '../models/job-summary.model';

/** A Job Summary page this young is shown without asking the server again. */
export const JOB_SUMMARY_FRESH_MS = 60 * 1000;
/** A page this young is still shown at once (then refreshed quietly); older pages are dropped. */
export const JOB_SUMMARY_KEEP_MS = 5 * 60 * 1000;
/** Pages kept per browser tab (9 tabs x a few pages / sorts / periods). */
export const JOB_SUMMARY_CACHE_SIZE = 60;

/**
 * Identity of a Job Summary page: everything the server reads. Preset periods are worked out on the
 * server from "now", so their dates are not part of the key; a custom range is.
 */
export function jobSummaryRequestKey(request: JobSummaryRequest): string {
  const custom = request.timeRange === TimeRangeEnum.custom;
  return JSON.stringify([
    request.orgId,
    request.operationId,
    request.status ?? null,
    request.timeRange,
    custom ? toIso(request.startDate) : null,
    custom ? toIso(request.endDate) : null,
    request.pageIndex,
    request.pageSize,
    request.sortBy ?? null,
    request.sortDirection ?? null
  ]);
}

/**
 * The first page (default sort) of every other tab for the same org, operation and period - what the
 * user most likely clicks next. Fetched in the background so that click is served from the cache.
 */
export function otherTabFirstPages(request: JobSummaryRequest): JobSummaryRequest[] {
  return JOB_SUMMARY_TABS
    .map(tab => tab.status)
    .filter(status => status !== request.status)
    .map(status => {
      const sort = JOB_SUMMARY_DEFAULT_SORT[status];
      const next: JobSummaryRequest = {
        orgId: request.orgId,
        operationId: request.operationId,
        timeRange: request.timeRange,
        status,
        pageIndex: 0,
        pageSize: request.pageSize
      };
      if (request.startDate) {
        next.startDate = request.startDate;
      }
      if (request.endDate) {
        next.endDate = request.endDate;
      }
      if (sort) {
        next.sortBy = sort.column;
        next.sortDirection = sort.direction;
      }
      return next;
    });
}

export interface CachedJobSummaryPage {
  value: JobSummaryResponse;
  /** Younger than JOB_SUMMARY_FRESH_MS: no need to ask the server. */
  fresh: boolean;
}

/** In-memory Job Summary pages for this browser tab (lost on reload). */
export class JobSummaryPageCache {
  private readonly pages = new Map<string, { value: JobSummaryResponse; at: number }>();

  constructor(private readonly maxEntries = JOB_SUMMARY_CACHE_SIZE) {}

  get(key: string, now = Date.now()): CachedJobSummaryPage | null {
    const entry = this.pages.get(key);
    if (!entry) {
      return null;
    }
    const age = now - entry.at;
    if (age >= JOB_SUMMARY_KEEP_MS) {
      this.pages.delete(key);
      return null;
    }
    return { value: entry.value, fresh: age < JOB_SUMMARY_FRESH_MS };
  }

  isFresh(key: string, now = Date.now()): boolean {
    return !!this.get(key, now)?.fresh;
  }

  set(key: string, value: JobSummaryResponse, now = Date.now()): void {
    this.pages.delete(key); // re-insert: newest last
    this.pages.set(key, { value, at: now });
    while (this.pages.size > this.maxEntries) {
      this.pages.delete(this.pages.keys().next().value);
    }
  }

  clear(): void {
    this.pages.clear();
  }

  get size(): number {
    return this.pages.size;
  }
}

function toIso(value: Date | string | undefined): string | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
}
